import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { DecimalShorthand } from '../../legacy-design-system/count-shorthand'
import { largeStatsCss, smallStatsCss } from '../../legacy-design-system/text'
import { css, cx } from '@emotion/css'
import {
  theme,
  IconWarning
} from '@contrast-security-inc/design-system-foundations'
import {
  getApplicationRouteCoverageRoute,
  getApplicationUrlActivityCoverage
} from '../route'
import { AgentLanguage, AgentLanguageValue } from '../agent-language'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'

const smdRoutesExercisedTooltipWidth = '296px'

const linkCss = css`
  &:hover {
    text-decoration: none;
  }
  &:focus {
    text-decoration: none;
  }
`

const numeratorCss = cx(
  largeStatsCss,
  css`
    color: ${theme.color.turquoise70};
    margin-left: -3px;
    @media (max-width: 991px) {
      font-size: 20px;
      margin-left: 0px;
    }
    @media (max-width: 767px) {
      font-size: 12px;
      margin-left: 0px;
    }
  `
)

const denominatorCss = cx(
  smallStatsCss,
  css`
    color: ${theme.color.neutral60};
    @media (max-width: 991px) {
      font-size: 20px;
      margin-left: 0px;
    }
    @media (max-width: 767px) {
      font-size: 12px;
      margin-left: 0px;
    }
  `
)

const descriptionCss = css`
  display: block;
  font-size: 14px;
  @media (max-width: 991px) {
    font-size: 12px;
    padding-left: 4px;
    vertical-align: 1px;
    display: inline-block;
  }

  @media (max-width: 767px) {
    display: block;
    padding-left: 0px;
  }
`

export interface RoutesShorthandProps {
  exercisedCount: number
  organizationID: string
  applicationID: string
  discoveredCount?: number
  discoveredByUrlActivity?: boolean
  applicationLanguage?: AgentLanguageValue
}

export function RoutesShorthandMetric({
  exercisedCount,
  organizationID,
  applicationID,
  discoveredCount,
  discoveredByUrlActivity,
  applicationLanguage
}: RoutesShorthandProps) {
  const intl = useIntl()
  return (
    <div>
      <a
        className={linkCss}
        href={
          discoveredByUrlActivity &&
          applicationLanguage === AgentLanguage.JAVA.getValue()
            ? getApplicationUrlActivityCoverage(organizationID, applicationID)
            : getApplicationRouteCoverageRoute(organizationID, applicationID)
        }
        data-testid="route-count"
      >
        <span className={numeratorCss}>
          <DecimalShorthand returnPlaneString count={exercisedCount} />
        </span>
        {!!discoveredCount && (
          <span className={denominatorCss}>
            /<DecimalShorthand returnPlaneString count={discoveredCount} />
          </span>
        )}
      </a>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: '5px',
          lineHeight: '10px'
        }}
      >
        <FormattedMessage
          id="detail_applicationOverview_routesExercised"
          defaultMessage="{count, plural, one{Route Exercised} other{Routes Exercised}}"
          values={{ count: exercisedCount }}
        >
          {(text) => <div className={descriptionCss} children={text} />}
        </FormattedMessage>

        <Tooltip
          body={intl.formatMessage({
            id: 'detail_applicationOverview_routesExercised_SessionMetadata_Tooltip'
          })}
          placement="bottom"
          style={{
            minWidth: smdRoutesExercisedTooltipWidth,
            maxWidth: smdRoutesExercisedTooltipWidth
          }}
        >
          <span>
            <IconWarning color="neutral60" size="sm" />
          </span>
        </Tooltip>
      </div>
    </div>
  )
}
